import React, { useEffect, useState } from "react";
import { vendorServices, vendorToggleService, vendorDeleteService, vendorServiceAvailability } from "@/lib/vendorApi";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Plus, Trash2, Wrench, Edit3, Eye, EyeOff, Tag, Share2 } from "lucide-react";
import AddServiceDialog from "./AddServiceDialog";
import ServiceAvailableFrom from "./ServiceAvailableFrom";
import ServiceShareSheet, { isShareableService } from "@/components/ServiceShareSheet";

export default function MyServices({ shops, refreshKey, onChanged, addSignal = 0, onAddHandled }) {
  const { formatApiError } = useApp();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sharing, setSharing] = useState(null);
  const share = (s) => (isShareableService(s) ? setSharing(s) : toast.info("Ye service abhi Inactive ya approval pending hai — pehle Activate karein, phir share karein."));
  useEffect(() => { if (addSignal > 0) { setEditing(null); setAddOpen(true); onAddHandled?.(); } }, [addSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const list = await vendorServices();
      setServices(list);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const toggle = async (svc) => {
    try {
      await vendorToggleService(svc.id, !(svc.active !== false));
      toast.success(svc.active !== false ? "Service deactivated" : "Service activated");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setAvail = async (svc) => {
    const next = svc.available === false;
    try {
      await vendorServiceAvailability(svc.id, next);
      toast.success(next ? "Service Available" : "Service Unavailable — search me label ke saath dikhegi");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (s) => {
    if (!window.confirm(`"${s.name}" ko permanently delete karein? Ye wapas nahi aayega. Sirf chhupane ke liye "Deactivate" use karein.`)) return;
    const id = s.id;
    try {
      await vendorDeleteService(id);
      toast.success("Deleted");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const shopName = (sid) => shops.find((s) => s.shop_id === sid)?.name || sid;

  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 sm:p-6 min-w-0" data-testid="my-services">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="font-display text-xl font-bold">My Services ({services.length})</h2>
          <p className="text-sm text-slate-500">Add and manage the local services you offer.</p>
        </div>
        <button onClick={() => { setEditing(null); setAddOpen(true); }} className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-4 py-2 text-sm font-semibold" data-testid="add-service-btn">
          <Plus className="w-4 h-4" /> Add service
        </button>
      </div>
      {loading ? (
        <div className="py-12 text-center text-slate-500 text-sm">Loading...</div>
      ) : services.length === 0 ? (
        <div className="py-12 text-center" data-testid="services-empty">
          <Wrench className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <div className="font-display text-lg font-bold">No services yet</div>
          <p className="text-sm text-slate-500 mt-1">Add your first service — plumbing, tuition, cleaning, etc.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {services.map((s) => {
            const active = s.active !== false;
            return (
              <div key={s.id} className={`min-w-0 flex flex-wrap sm:flex-nowrap items-start gap-3 border rounded-xl p-3 ${active ? "border-slate-100" : "border-slate-200 bg-slate-50 opacity-70"}`} data-testid={`vendor-service-${s.id}`}>
                <img src={s.image || "https://placehold.co/64x64/f1f5f9/94a3b8?text=Svc"} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-slate-900 truncate min-w-0 max-w-full">{s.name}</div>
                    <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`} data-testid={`service-status-${s.id}`}>{active ? "Active" : "Inactive"}</span>
                    <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-full ${s.available === false ? "bg-rose-100 text-rose-700" : "bg-emerald-50 text-emerald-700"}`} data-testid={`service-availability-${s.id}`}>{s.available === false ? "Unavailable" : "Available"}</span>
                  </div>
                  <div className="text-xs text-slate-500 truncate flex items-center gap-1"><Tag className="w-3 h-3" /> {s.category}{s.subcategory ? ` · ${s.subcategory}` : ""}</div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">{s.provider || shopName(s.shop_id)}{s.provider && s.provider !== shopName(s.shop_id) ? ` · ${shopName(s.shop_id)}` : ""}</div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-600">
                    <span className="font-bold text-emerald-800">{s.price_from == null ? "Fee as discussed" : `₹${s.price_from}`}</span>
                    <span className="text-slate-400">{s.unit || ""}</span>
                    {s.duration && <span className="text-slate-400">· {s.duration}</span>}
                  </div>
                  {s.available === false && <ServiceAvailableFrom key={`${s.id}-${s.available_from || ""}`} svc={s} formatApiError={formatApiError} onSaved={() => { load(); onChanged?.(); }} />}
                </div>
                <div className="w-full sm:w-auto flex flex-row flex-wrap sm:flex-nowrap sm:flex-col gap-1.5 flex-shrink-0">
                  <button onClick={() => share(s)} aria-disabled={!isShareableService(s)} className={`inline-flex items-center gap-1 rounded-full border max-sm:flex-auto justify-center px-2.5 py-1 text-xs sm:text-[11px] font-semibold min-h-[40px] sm:min-h-[32px] ${isShareableService(s) ? "border-emerald-200 text-emerald-800 hover:bg-emerald-50" : "border-slate-200 text-slate-400"}`} data-testid={`share-service-${s.id}`}>
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </button>
                  <button onClick={() => { setEditing(s); setAddOpen(true); }} className="inline-flex items-center gap-1 rounded-full border border-slate-200 max-sm:flex-auto justify-center px-2.5 py-1 text-xs sm:text-[11px] font-semibold text-slate-700 hover:border-emerald-500 hover:text-emerald-700 min-h-[40px] sm:min-h-[32px]" data-testid={`edit-service-${s.id}`}>
                    <Edit3 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => setAvail(s)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 max-sm:flex-auto justify-center px-2.5 py-1 text-xs sm:text-[11px] font-semibold text-slate-700 hover:border-emerald-500 hover:text-emerald-700 min-h-[40px] sm:min-h-[32px]" data-testid={`availability-service-${s.id}`}>
                    {s.available === false ? "Mark Available" : "Mark Unavailable"}
                  </button>
                  <button onClick={() => toggle(s)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 max-sm:flex-auto justify-center px-2.5 py-1 text-xs sm:text-[11px] font-semibold text-slate-700 hover:border-amber-400 hover:text-amber-700 min-h-[40px] sm:min-h-[32px]" data-testid={`toggle-service-${s.id}`}>
                    {active ? <><EyeOff className="w-3.5 h-3.5" /> Deactivate</> : <><Eye className="w-3.5 h-3.5" /> Activate</>}
                  </button>
                  <button onClick={() => del(s)} className="inline-flex items-center gap-1 rounded-full border border-rose-200 max-sm:flex-auto justify-center px-2.5 py-1 text-xs sm:text-[11px] font-semibold text-rose-600 hover:bg-rose-50 min-h-[40px] sm:min-h-[32px]" data-testid={`delete-service-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AddServiceDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        shops={shops}
        service={editing}
        onSaved={() => { setAddOpen(false); setEditing(null); load(); onChanged?.(); }}
      />
      <ServiceShareSheet open={!!sharing} onClose={() => setSharing(null)} svc={sharing} />
    </div>
  );
}
